import { useMemo } from "react";

const rnd = (seed, i) => {
  const x = Math.sin(seed * 999.7 + i * 77.31) * 43758.5453;
  return x - Math.floor(x);
};

export const AbstractVisual = ({ hue = 222, seed = 1, className = "", children }) => {
  const art = useMemo(() => {
    const h2 = (hue + 45 + rnd(seed, 1) * 40) % 360;
    const x1 = 15 + rnd(seed, 2) * 55, y1 = 10 + rnd(seed, 3) * 45;
    const x2 = 20 + rnd(seed, 4) * 60, y2 = 45 + rnd(seed, 5) * 45;
    const ringX = 20 + rnd(seed, 6) * 60, ringY = 15 + rnd(seed, 7) * 55;
    const ringSize = 30 + rnd(seed, 8) * 45;
    return { h2, x1, y1, x2, y2, ringX, ringY, ringSize };
  }, [hue, seed]);

  return (
    <div className={`absolute inset-0 overflow-hidden bg-[#07070b] ${className}`} aria-hidden="true">
      <div
        className="absolute inset-0 transition-transform duration-[1.4s] ease-out group-hover:scale-110"
        style={{
          background: `
            radial-gradient(circle at ${art.x1}% ${art.y1}%, hsla(${hue}, 75%, 55%, 0.34), transparent 52%),
            radial-gradient(circle at ${art.x2}% ${art.y2}%, hsla(${art.h2}, 70%, 45%, 0.22), transparent 55%),
            linear-gradient(160deg, #0a0a10 0%, #050508 100%)`,
        }}
      />
      <div
        className="absolute rounded-full border border-white/[0.13]"
        style={{
          width: `${art.ringSize}%`, paddingBottom: `${art.ringSize}%`,
          left: `${art.ringX}%`, top: `${art.ringY}%`,
          transform: "translate(-50%, -50%)",
          boxShadow: `0 0 90px hsla(${hue}, 80%, 60%, 0.22) inset, 0 0 60px hsla(${hue}, 80%, 60%, 0.12)`,
        }}
      />
      <div
        className="absolute rounded-full blur-3xl animate-float-slow"
        style={{
          width: "45%", paddingBottom: "45%",
          left: `${art.x2}%`, top: `${art.y1}%`,
          background: `hsla(${hue}, 85%, 60%, 0.14)`,
        }}
      />
      <div
        className="absolute inset-0 opacity-[0.5]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage: "radial-gradient(ellipse 80% 70% at 50% 45%, black 25%, transparent 78%)",
          WebkitMaskImage: "radial-gradient(ellipse 80% 70% at 50% 45%, black 25%, transparent 78%)",
        }}
      />
      <div className="noise-overlay" />
      {children}
    </div>
  );
};

export const ParticleField = ({ count = 22, hue = 222 }) => {
  const particles = useMemo(
    () => Array.from({ length: count }, (_, i) => ({
      left: rnd(i + 3, 1) * 100,
      bottom: rnd(i + 3, 2) * 60,
      size: 1 + rnd(i + 3, 3) * 2.5,
      duration: 7 + rnd(i + 3, 4) * 9,
      delay: rnd(i + 3, 5) * 9,
    })),
    [count]
  );
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
      {particles.map((p) => (
        <span
          key={`${p.left}-${p.bottom}-${p.delay}`}
          className="absolute rounded-full"
          style={{
            left: `${p.left}%`, bottom: `${p.bottom}%`,
            width: p.size, height: p.size,
            background: `hsla(${hue}, 80%, 75%, 0.75)`,
            boxShadow: `0 0 8px hsla(${hue}, 80%, 70%, 0.8)`,
            animation: `particle-rise ${p.duration}s linear ${p.delay}s infinite`,
          }}
        />
      ))}
    </div>
  );
};
